// ============================================
// App Router - Route Definitions
// ============================================

import { Routes, Route } from 'react-router-dom';
import { ADMIN_ROUTES, PERMISSIONS } from '@conai/shared-constants';
import { RequireAuth, RequirePermission, RequireRole, GuestOnly } from './guards';
import { MainLayout } from '../components/layout';

// Import auth pages
import {
  LoginPage,
  RegisterPage,
  StoreSelectionPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '../pages/auth';

// Import actual pages
import {
  InventoryPage,
  AdjustmentHistoryPage,
  InventoryDriftPage,
  InventoryLossStatsPage,
  BatchesPage,
  StocktakePage,
} from '../pages/inventory';
import { ProductListPage } from '../pages/products';
import { DashboardPage } from '../pages/dashboard';
import { StoresPage } from '../pages/stores';
import { TransactionsPage } from '../pages/transactions';
import { CustomersPage } from '../pages/customers';
import { AgeVerificationsPage } from '../pages/age-verifications';
import { RestrictedSalesPage } from '../pages/restricted-sales';
import { EmployeesPage } from '../pages/employees';
import { SuppliersPage } from '../pages/suppliers';
import { PurchaseOrdersPage } from '../pages/purchase-orders';
import { ReportsPage, DailyClosePage } from '../pages/reports';
import { OrdersPage } from '../pages/orders';
import { CategoriesPage } from '../pages/categories';
import { PromotionsPage } from '../pages/promotions';
import { LoyaltyPage } from '../pages/loyalty';
import { AccessControlPage } from '../pages/access-control';
import { AtWorkPage, AtWorkDoorDetailPage } from '../pages/atwork';
import { AuditLogsPage } from '../pages/audit-logs';
import { SettingsPage } from '../pages/settings';
import { RolesPage } from '../pages/roles';
import { FeedbackPage } from '../pages/feedback';
import { DisputesPage } from '../pages/disputes';
import { ReturnsPage } from '../pages/returns';

// 404 page
function NotFoundPage() {
  return (
    <div className="flex min-h-[calc(100vh-64px)] items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-slate-800">404</h1>
        <p className="mt-4 text-xl text-slate-600">Page not found</p>
        <a
          href={ADMIN_ROUTES.DASHBOARD}
          className="mt-6 inline-block text-primary-600 hover:text-primary-700"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

// ============================================
// Main Router
// ============================================

export function AppRouter() {
  return (
    <Routes>
      {/* Public routes (outside MainLayout) */}
      <Route
        path={ADMIN_ROUTES.LOGIN}
        element={
          <GuestOnly>
            <LoginPage />
          </GuestOnly>
        }
      />
      <Route
        path="/register"
        element={
          <GuestOnly>
            <RegisterPage />
          </GuestOnly>
        }
      />
      <Route
        path={ADMIN_ROUTES.FORGOT_PASSWORD}
        element={
          <GuestOnly>
            <ForgotPasswordPage />
          </GuestOnly>
        }
      />
      <Route
        path={ADMIN_ROUTES.RESET_PASSWORD}
        element={
          <GuestOnly>
            <ResetPasswordPage />
          </GuestOnly>
        }
      />

      {/* Store Selection (requires auth but outside main layout) */}
      <Route
        path="/select-store"
        element={
          <RequireAuth>
            <StoreSelectionPage />
          </RequireAuth>
        }
      />

      {/* Protected routes (inside MainLayout) */}
      <Route
        element={
          <RequireAuth>
            <MainLayout />
          </RequireAuth>
        }
      >
        {/* Dashboard */}
        <Route path={ADMIN_ROUTES.DASHBOARD} element={<DashboardPage />} />

        {/* Stores */}
        <Route
          path={ADMIN_ROUTES.STORES}
          element={
            <RequirePermission permission={PERMISSIONS.STORES_VIEW}>
              <StoresPage />
            </RequirePermission>
          }
        />

        {/* Products */}
        <Route
          path={ADMIN_ROUTES.PRODUCTS}
          element={
            <RequirePermission permission={PERMISSIONS.PRODUCTS_VIEW}>
              <ProductListPage />
            </RequirePermission>
          }
        />

        {/* Categories */}
        <Route
          path={ADMIN_ROUTES.CATEGORIES}
          element={
            <RequirePermission permission={PERMISSIONS.CATEGORIES_VIEW}>
              <CategoriesPage />
            </RequirePermission>
          }
        />

        {/* Inventory */}
        <Route
          path={ADMIN_ROUTES.INVENTORY}
          element={
            <RequirePermission permission={PERMISSIONS.INVENTORY_VIEW}>
              <InventoryPage />
            </RequirePermission>
          }
        />

        {/* Lịch sử điều chỉnh tồn kho — GET /inventory/adjustments/ chỉ mở cho
            AI_ADMIN & STORE_ADMIN theo API_REFERENCE. Gác bằng ROLE chứ không
            bằng INVENTORY_ADJUST: quyền adjust giờ OPERATOR cũng có (BE xác
            nhận 29/08) nhưng đọc LỊCH SỬ thì không — dùng permission là
            OPERATOR mở được trang chỉ để nhận 403. */}
        <Route
          path={ADMIN_ROUTES.INVENTORY_ADJUSTMENTS}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <AdjustmentHistoryPage />
            </RequireRole>
          }
        />

        {/* Đối soát lệch tồn — GET /inventory/drift/. Cùng lý do gác ROLE như
            trang lịch sử: đây là dữ liệu đối soát, không phải thao tác quầy. */}
        <Route
          path={ADMIN_ROUTES.INVENTORY_DRIFT}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <InventoryDriftPage />
            </RequireRole>
          }
        />

        {/* Thống kê hao hụt theo nguyên nhân (#220) — GET /inventory/loss-stats/,
            cùng cách gác ROLE như các trang tra cứu tồn kho khác. */}
        <Route
          path={ADMIN_ROUTES.INVENTORY_LOSS_STATS}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <InventoryLossStatsPage />
            </RequireRole>
          }
        />

        {/* Lô & hạn sử dụng (#32) — cùng cách gác ROLE như hai trang trên: đây
            là sổ lô của kho, không phải thao tác quầy. */}
        <Route
          path={ADMIN_ROUTES.INVENTORY_BATCHES}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <BatchesPage />
            </RequireRole>
          }
        />

        {/* Kiểm kê định kỳ (#30) — chốt phiên sinh bút toán thật trên toàn kho
            nên gác đúng hai vai quản trị, không mở cho OPERATOR. */}
        <Route
          path={ADMIN_ROUTES.INVENTORY_STOCKTAKES}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <StocktakePage />
            </RequireRole>
          }
        />

        {/* Stock Imports / Purchase Orders */}
        <Route
          path={ADMIN_ROUTES.STOCK_IMPORTS}
          element={
            <RequirePermission permission={PERMISSIONS.STOCK_IMPORTS_VIEW}>
              <PurchaseOrdersPage />
            </RequirePermission>
          }
        />

        {/* Suppliers */}
        <Route
          path={ADMIN_ROUTES.SUPPLIERS}
          element={
            <RequirePermission permission={PERMISSIONS.INVENTORY_VIEW}>
              <SuppliersPage />
            </RequirePermission>
          }
        />

        {/* Orders */}
        <Route
          path={ADMIN_ROUTES.ORDERS}
          element={
            <RequirePermission permission={PERMISSIONS.ORDERS_VIEW}>
              <OrdersPage />
            </RequirePermission>
          }
        />

        {/* Payments / Transactions */}
        <Route
          path={ADMIN_ROUTES.PAYMENTS}
          element={
            <RequirePermission permission={PERMISSIONS.PAYMENTS_VIEW}>
              <TransactionsPage />
            </RequirePermission>
          }
        />

        {/* Customers */}
        <Route
          path={ADMIN_ROUTES.CUSTOMERS}
          element={
            <RequirePermission permission={PERMISSIONS.CUSTOMERS_VIEW}>
              <CustomersPage />
            </RequirePermission>
          }
        />

        {/* Duyệt xác minh 18+ (STORE_ADMIN | AI_ADMIN) */}
        <Route
          path={ADMIN_ROUTES.AGE_VERIFICATIONS}
          element={
            <RequirePermission permission={PERMISSIONS.CUSTOMERS_AGE_VERIFY}>
              <AgeVerificationsPage />
            </RequirePermission>
          }
        />

        {/* Sổ bằng chứng bán hàng 18+.
            Gate theo ROLE chứ không theo permission string: BE xác nhận
            18/08/2026 rằng hệ thống permission code bên đó mới là stub
            (`User.permissions` có tồn tại nhưng không endpoint nào đọc), và hai
            endpoint audit gate bằng `role in ("STORE_ADMIN", "AI_ADMIN")`.
            Dựng một scope string ở FE cho khớp với thứ BE không kiểm là tự tạo
            ảo giác an toàn. */}
        <Route
          path={ADMIN_ROUTES.RESTRICTED_SALES}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <RestrictedSalesPage />
            </RequireRole>
          }
        />

        {/* Góp ý của khách (bug #208).

            Gate theo ROLE, cùng lý do đã ghi ở Restricted Sales ngay trên: BE
            bảo vệ `/feedback/staff/*` bằng permission class `IsStoreAdmin` —
            tức là theo VAI TRÒ — chứ không đọc permission string nào. Trong
            `PERMISSIONS` cũng chưa có mã nào cho feedback, và bịa ra một mã ở FE
            cho khớp với thứ BE không kiểm chỉ tạo ảo giác an toàn.

            AI_ADMIN có mặt ở đây vì trong mô hình quyền của FE nó là siêu quản
            trị (`ROLE_PERMISSIONS.AI_ADMIN = Object.values(PERMISSIONS)`), nên
            bỏ nó ra sẽ khiến đây là màn DUY NHẤT của app mà AI_ADMIN không mở
            được. Nếu `IsStoreAdmin` của BE thực sự chỉ chấp nhận đúng
            `role == "STORE_ADMIN"` thì thu về `['STORE_ADMIN']` — sửa đúng dòng
            dưới đây. */}
        <Route
          path={ADMIN_ROUTES.FEEDBACK}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <FeedbackPage />
            </RequireRole>
          }
        />

        {/* Khiếu nại hoá đơn sau bán (sheet 2 #58/#60).
            Gác theo ROLE cùng lý do với FEEDBACK ở trên: disputes.service ghi
            rõ "chỉ store-admin" nhưng PERMISSIONS không có mã nào cho khiếu
            nại — bịa mã ở FE cho khớp thứ BE không kiểm chỉ tạo ảo giác an
            toàn. Ở đây còn nặng hơn góp ý: chốt một khiếu nại là HOÀN TIỀN
            THẬT, nên đừng nới guard xuống OPERATOR. */}
        <Route
          path={ADMIN_ROUTES.DISPUTES}
          element={
            <RequireRole allowedRoles={['STORE_ADMIN', 'AI_ADMIN']}>
              <DisputesPage />
            </RequireRole>
          }
        />

        {/* Đổi/trả tại quầy (sheet 2 #55/#56/#59).
            OPERATOR CÓ mặt ở đây là cố ý — BE tách quyền 2 tầng: việc ở quầy
            (quét mã, xem, nhận hàng) cho OPERATOR trở lên, còn duyệt/từ chối/
            hoàn tiền là quyết định về tiền, chặn riêng BÊN TRONG trang bằng
            `canDecideMoney`. Siết route lên STORE_ADMIN sẽ khiến nhân viên
            quầy không quét nổi mã QR trên điện thoại khách. */}
        <Route
          path={ADMIN_ROUTES.RETURNS}
          element={
            <RequireRole allowedRoles={['OPERATOR', 'STORE_ADMIN', 'AI_ADMIN']}>
              <ReturnsPage />
            </RequireRole>
          }
        />

        {/* Users / Employees */}
        <Route
          path={ADMIN_ROUTES.USERS}
          element={
            <RequirePermission permission={PERMISSIONS.USERS_VIEW}>
              <EmployeesPage />
            </RequirePermission>
          }
        />

        {/* Promotions */}
        <Route
          path={ADMIN_ROUTES.PROMOTIONS}
          element={
            <RequirePermission permission={PERMISSIONS.PROMOTIONS_VIEW}>
              <PromotionsPage />
            </RequirePermission>
          }
        />

        {/* Loyalty */}
        <Route
          path={ADMIN_ROUTES.LOYALTY}
          element={
            <RequirePermission permission={PERMISSIONS.LOYALTY_VIEW}>
              <LoyaltyPage />
            </RequirePermission>
          }
        />

        {/* Access Control */}
        <Route
          path={ADMIN_ROUTES.ACCESS_CONTROL}
          element={
            <RequirePermission permission={PERMISSIONS.ACCESS_VIEW}>
              <AccessControlPage />
            </RequirePermission>
          }
        />

        {/* Nedap AtWork - Door Access Control */}
        <Route
          path={ADMIN_ROUTES.ATWORK}
          element={
            <RequirePermission permission={PERMISSIONS.ATWORK_VIEW}>
              <AtWorkPage />
            </RequirePermission>
          }
        />
        <Route
          path={ADMIN_ROUTES.ATWORK_DOOR_DETAIL}
          element={
            <RequirePermission permission={PERMISSIONS.ATWORK_VIEW}>
              <AtWorkDoorDetailPage />
            </RequirePermission>
          }
        />

        {/* Reports */}
        <Route
          path={ADMIN_ROUTES.REPORTS}
          element={
            <RequirePermission permission={PERMISSIONS.REPORTS_SALES}>
              <ReportsPage />
            </RequirePermission>
          }
        />

        {/* Chốt cuối ngày (sheet 2 mục #53). Cùng permission với Reports:
            đây vẫn là đọc số liệu bán hàng, chỉ khác cách cắt ngày. */}
        <Route
          path={ADMIN_ROUTES.REPORTS_DAILY_CLOSE}
          element={
            <RequirePermission permission={PERMISSIONS.REPORTS_SALES}>
              <DailyClosePage />
            </RequirePermission>
          }
        />

        {/* Settings */}
        <Route
          path={ADMIN_ROUTES.SETTINGS}
          element={
            <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW}>
              <SettingsPage />
            </RequirePermission>
          }
        />

        {/* Audit Logs */}
        <Route
          path={ADMIN_ROUTES.AUDIT_LOGS}
          element={
            <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW}>
              <AuditLogsPage />
            </RequirePermission>
          }
        />

        {/* Roles */}
        <Route
          path="/roles"
          element={
            <RequirePermission permission={PERMISSIONS.USERS_VIEW}>
              <RolesPage />
            </RequirePermission>
          }
        />

        {/* 404 - Not Found (inside layout) */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
